import React, { useState } from "react";
import { ScanLine, Send, Loader2, CheckCircle2, Sparkles, Barcode, Receipt, Apple } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_LEN = 1000;

const IDEAS = [
  { icon: Barcode, text: "Scan a barcode to compare prices nearby" },
  { icon: Receipt, text: "Snap a receipt to track spending" },
  { icon: Apple, text: "Check nutrition & allergens instantly" },
];

const ThankYou = ({ onAgain }) => (
  <div className="text-center py-6 space-y-3 animate-in fade-in zoom-in-95" data-testid="scan-feedback-success">
    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
    <h2 className="text-lg font-bold text-gray-900">Thank you!</h2>
    <p className="text-sm text-gray-600 max-w-xs mx-auto">
      Your feedback has been received and will help shape how the Scan feature works.
    </p>
    <Button variant="outline" size="sm" onClick={onAgain} data-testid="scan-feedback-again-btn">
      Share another idea
    </Button>
  </div>
);

const Scan = () => {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const comment = text.trim();
    if (!comment) { setError("Please tell us what you'd like Scan to do."); return; }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/scan/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not send feedback");
      setText("");
      setDone(true);
    } catch (err) {
      setError(err.message || "Could not send feedback. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-5 pb-24" data-testid="scan-page">
      <div className="rounded-2xl bg-gray-950 text-white p-6 relative overflow-hidden" data-testid="scan-coming-soon">
        <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-green-500/20 blur-2xl" />
        <span className="inline-flex items-center gap-1 rounded-full bg-green-500/15 text-green-400 text-[11px] font-semibold px-2.5 py-1 mb-3">
          <Sparkles className="h-3 w-3" /> Coming Soon
        </span>
        <div className="flex items-center gap-3 mb-2">
          <ScanLine className="h-8 w-8 text-green-400" />
          <h1 className="text-2xl font-bold">Scan is on its way</h1>
        </div>
        <p className="text-sm text-gray-300 leading-relaxed">
          We're building a scanner to make shopping faster and cheaper. Before we finish it,
          we want to hear from you: what should it do?
        </p>
        <ul className="mt-4 space-y-2">
          {IDEAS.map(({ icon: Icon, text: t }) => (
            <li key={t} className="flex items-center gap-2 text-xs text-gray-400">
              <Icon className="h-3.5 w-3.5 text-green-400 shrink-0" /> {t}
            </li>
          ))}
        </ul>
      </div>

      <Card className="border-gray-200">
        <CardContent className="p-5">
          {done ? (
            <ThankYou onAgain={() => setDone(false)} />
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="scan-feedback-form">
              <label htmlFor="scan-feedback" className="block text-sm font-semibold text-gray-900">
                What would you like the Scan feature to do?
              </label>
              <textarea
                id="scan-feedback"
                value={text}
                onChange={(e) => { setText(e.target.value.slice(0, MAX_LEN)); setError(""); }}
                rows={5}
                placeholder="e.g. Scan a product and show me the cheapest store near me…"
                className="w-full rounded-xl border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                data-testid="scan-feedback-input"
              />
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span data-testid="scan-feedback-count">{text.length}/{MAX_LEN}</span>
                <span>{user ? "Sent with your account" : "Sent anonymously as a guest"}</span>
              </div>
              {error && <p className="text-xs text-red-600" data-testid="scan-feedback-error">{error}</p>}
              <Button
                type="submit"
                disabled={submitting || !text.trim()}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                data-testid="scan-feedback-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send feedback
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Scan;
